import { useQuery } from '@tanstack/react-query'

interface GoogleAdsPreviewMeta {
  id: string
  filename?: string
  created_at?: string
  updated_at?: string
  [key: string]: any
}

// Fetch function for Google Ads preview metadata
// Exported for prefetching
export async function fetchPreviewMeta(previewId: string): Promise<GoogleAdsPreviewMeta | null> {
  const response = await fetch(`/api/googleads-previews/${previewId}`, { cache: 'no-store' })

  if (!response.ok) {
    throw new Error('Failed to fetch preview metadata')
  }

  return response.json()
}

// Query key factory
export const googleAdsPreviewMetaKeys = {
  all: ['googleAdsPreviewMeta'] as const,
  byId: (previewId: string) => [...googleAdsPreviewMetaKeys.all, previewId] as const,
}

/**
 * React Query hook for fetching Google Ads preview metadata
 */
export function useGoogleAdsPreviewMeta(previewId?: string | null) {
  const query = useQuery({
    queryKey: googleAdsPreviewMetaKeys.byId(previewId || ''),
    queryFn: () => fetchPreviewMeta(previewId!),
    enabled: !!previewId,
    staleTime: 0, // Always consider stale so tab visibility changes are reflected immediately
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    refetchOnMount: true, // Always refetch on mount (serves cached data instantly, updates in background)
    refetchOnWindowFocus: false, // Don't refetch on window focus
  })

  return {
    preview: query.data || null,
    loading: query.isLoading,
    error: query.error ? (query.error as Error).message : null,
    refetch: query.refetch,
  }
}

